<script setup lang="ts">
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import { ref, computed } from "vue";
import {
  AtomText,
  useToast,
  AtomButton,
  MolCollapseIndicator,
  AtomBadge,
  AtomLink,
} from "@chatfood/design-system";
import type { IDiscountDetailsProps } from "./props";
import { useLocale } from "~/composables/use-locale";
import { useBusinessStore } from "~/stores/business";
import { useBasketStore } from "~/stores/basket";
import { useDate } from "~/composables/use-date";
import { I18nTranslate } from "~/components/i18n-translate";

const css = bemBuilder("discount-details");

const props = defineProps<IDiscountDetailsProps>();

const emits = defineEmits<{ close: [event: MouseEvent] }>();

const business = useBusinessStore();
const basket = useBasketStore();

const { t, locale } = useLocale();
const { getReadableDate } = useDate();
const { toastUnexpectedError, toastSuccess } = useToast();

const appliedDiscountCode = computed(() => basket.discount?.code);
const discountLoading = ref<string>();
const isTermsCollapsed = ref(false);

const termsToggle = () => {
  isTermsCollapsed.value = !isTermsCollapsed.value;
};

function formatMoney(amount: number, currency: string) {
  return toMoney(amount, currency, { locale: locale.value });
}

async function remove(discountCode: string) {
  discountLoading.value = discountCode;

  try {
    await basket.removeDiscount();
  } catch {
    toastUnexpectedError();
  } finally {
    discountLoading.value = undefined;
  }
}

async function apply(discountCode: string, event: MouseEvent) {
  discountLoading.value = discountCode;

  try {
    if (basket.discount) {
      await basket.removeDiscount();
    }

    await basket.applyDiscount(discountCode);

    toastSuccess(t("components.discounts_list.success_toast"));
    emits("close", event);
  } catch {
    toastUnexpectedError();
  } finally {
    discountLoading.value = undefined;
  }
}

function isEligibleForMultipleFirstOrders(
  firstOrdersOnly: boolean,
  maxUsesPerCustomer?: number,
): maxUsesPerCustomer is number {
  return (
    firstOrdersOnly &&
    maxUsesPerCustomer !== undefined &&
    maxUsesPerCustomer > 1
  );
}

function isEligibleForSingleFirstOrder(
  firstOrdersOnly: boolean,
  maxUsesPerCustomer?: number,
): maxUsesPerCustomer is number {
  return (
    firstOrdersOnly &&
    maxUsesPerCustomer !== undefined &&
    maxUsesPerCustomer === 1
  );
}

const rulesBuilder = computed(() => {
  const rules = [];

  if (props.discount.minimumOrderValue) {
    rules.push(
      t("components.discounts_list.rules.min_order", {
        amount: formatMoney(
          props.discount.minimumOrderValue,
          business.currency,
        ),
      }),
    );
  }

  if (props.discount.discountedProducts.length > 0) {
    rules.push(t("components.discounts_list.rules.selected_items_only"));
  }

  if (props.discount.maximumDiscount) {
    rules.push(
      t("components.discounts_list.rules.max_discount", {
        amount: formatMoney(props.discount.maximumDiscount, business.currency),
      }),
    );
  }

  if (
    isEligibleForMultipleFirstOrders(
      props.discount.firstOrdersOnly,
      props.discount.maxUsesPerCustomer,
    )
  ) {
    rules.push(
      t("components.discounts_list.rules.first_orders", {
        quantity: props.discount.maxUsesPerCustomer,
      }),
    );
  } else if (
    isEligibleForSingleFirstOrder(
      props.discount.firstOrdersOnly,
      props.discount.maxUsesPerCustomer,
    )
  ) {
    rules.push(t("components.discounts_list.rules.first_order"));
  }

  if (props.discount.maxUsesPerCustomer) {
    rules.push(
      t("components.discounts_list.rules.max_uses", {
        amount: props.discount.maxUsesPerCustomer,
      }),
    );
  }

  if (props.discount.maxUsagePerOrder) {
    rules.push(
      t("components.discounts_list.rules.max_usage_per_order", {
        amount: props.discount.maxUsagePerOrder,
      }),
    );
  }

  return rules;
});
</script>

<template>
  <div :class="css('')">
    <div :class="css('details')">
      <div :class="css('title')">
        <AtomText type="subtitle" data-test="name" color="color-text-headline">
          {{ props.discount.name }}
        </AtomText>
        <AtomBadge
          v-if="props.discount.unit === 'percentage'"
          :value="
            t('components.discount_details.flag_percentage_amount', {
              amount: props.discount.amount,
            })
          "
          :class="css('badge')"
          state="danger"
          data-test="percentage-badge"
        />
        <AtomBadge
          v-else
          :value="
            t('components.discount_details.flag_currency_amount', {
              amount: toMoney(props.discount.amount.value, business.currency),
            })
          "
          :class="css('badge')"
          state="danger"
          data-test="currency-badge"
        />
      </div>
      <div :class="css('subtitle')">
        <AtomText
          v-if="!props.discount.end"
          tag="div"
          type="body-bold"
          data-test="code"
          color="color-text-secondary"
        >
          {{ props.discount.code }}
        </AtomText>
        <I18nTranslate
          v-else
          data-test="expiry-code"
          keypath="components.discount_details.validity_date"
        >
          <template #discountCode>
            <span :class="css('code')">{{ props.discount.code }}</span>
          </template>
          <template #date>
            <span>
              {{
                getReadableDate(props.discount.end.toString(), "Do MMMM, YYYY")
              }}
            </span>
          </template>
        </I18nTranslate>
      </div>
      <div v-if="rulesBuilder.length" data-test="terms-conditions">
        <AtomLink
          to=""
          color="color-primary"
          :class="css('terms')"
          data-test="terms-toggle"
          @click.prevent="termsToggle"
        >
          {{ t("components.discount_details.tcs_title") }}
          <MolCollapseIndicator
            :class="css('collapse-indicator')"
            :border-less="true"
            theme="ghost"
            variant="primary"
            :collapsed="!isTermsCollapsed"
          />
        </AtomLink>
        <Transition name="fade-slide">
          <div
            v-if="isTermsCollapsed"
            :class="css('terms-list')"
            data-test="terms-list"
          >
            <AtomText
              v-for="(rule, index) in rulesBuilder"
              :key="index"
              tag="div"
              color="color-text-secondary"
            >
              {{ rule }}
            </AtomText>
          </div>
        </Transition>
      </div>
    </div>
    <AtomButton
      v-if="appliedDiscountCode !== props.discount.code"
      :is-loading="discountLoading === props.discount.code"
      size="medium"
      wide
      variant="primary"
      theme="ghost"
      data-test="apply-cta"
      :class="css('discount-cta')"
      @click="apply(props.discount.code, $event)"
    >
      {{ t("components.discount_details.cta_apply") }}
    </AtomButton>
    <AtomButton
      v-else
      :is-loading="discountLoading === props.discount.code"
      theme="ghost"
      size="medium"
      data-test="remove-cta"
      wide
      variant="danger"
      @click="remove(props.discount.code)"
    >
      {{ t("components.discount_details.cta_remove") }}
    </AtomButton>
  </div>
</template>

<style lang="scss" scoped>
.discount-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-md);
  width: 100%;
  padding-bottom: var(--spacing-lg);

  &:last-child {
    padding-bottom: 0;
    border-bottom: none;
  }

  &__title {
    display: flex;
    justify-content: space-between;
  }

  &__badge {
    min-width: fit-content;
  }

  &__details {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &__amount-label {
    display: inline-flex;
    padding: var(--spacing-xs) var(--spacing-sm);
    border-radius: var(--radii-card-xs);
    background-color: var(--color-error);
    min-width: 55px;
    height: fit-content;
  }

  &__subtitle {
    display: flex;
    margin-top: var(--spacing-xs);
  }

  &__code {
    color: var(--color-text-secondary);
    font-weight: 600;
    font-family: var(--text-base-font-family);
  }

  &__terms {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    cursor: pointer;
    margin-top: var(--spacing-sm);
  }

  &__collapse-indicator {
    width: 16px;
    height: 16px;
  }
}

.fade-slide-enter-active {
  transition: all 3s ease-out;
}

.fade-slide-leave-active {
  transition: all 0.3s ease-out;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  max-height: 1px;
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  max-height: 1000px;
}
</style>
